import React, { useState, useRef, useEffect } from "react";
import { CheckCircle, XCircle, Clock, FileText, X } from "@phosphor-icons/react";
import { PaymentStatusBadge } from "@/components/StatusBadge";
import { API } from "@/lib/api";

const fmtCur = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const STATUS_PILL = {
  pending:  { cls: "bg-slate-100 text-slate-600 border-slate-300",   label: "Pending",  Icon: Clock },
  approved: { cls: "bg-emerald-50 text-emerald-700 border-emerald-300", label: "Released", Icon: CheckCircle },
  rejected: { cls: "bg-rose-50 text-rose-700 border-rose-300",       label: "Rejected", Icon: XCircle },
};

function ReleasedPopover({ req, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);
  const proofUrl = req.release_proof_file_id ? `${API}/invoices/${req.release_proof_file_id}` : "";
  return (
    <div
      ref={ref}
      className="absolute left-0 top-full mt-1 z-50 w-[260px] bg-white border border-slate-200 rounded-md shadow-xl p-3 text-xs"
      data-testid={`released-popover-${req.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="font-semibold text-slate-900">{req.payment_type}</div>
        <button onClick={onClose} className="p-0.5 hover:bg-slate-100 rounded"><X size={12} /></button>
      </div>
      <div className="space-y-1.5">
        <div className="flex justify-between"><span className="text-slate-500">Gross</span><span className="font-mono font-semibold">{fmtCur(req.amount)}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">TDS ({req.tds_rate}%)</span><span className="font-mono text-rose-700">− {fmtCur(req.tds_amount)}</span></div>
        <div className="flex justify-between pb-1 border-b border-slate-100"><span className="text-slate-500">Net paid</span><span className="font-mono font-semibold text-emerald-700">{fmtCur(req.net_amount)}</span></div>
        {req.released_on && (
          <div><span className="text-slate-500">Released on: </span><span className="font-medium text-slate-800">{new Date(req.released_on).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span></div>
        )}
        <div>
          <div className="text-slate-500 uppercase tracking-wider text-[10px]">UTR / Reference</div>
          <div className="font-mono text-slate-900 break-all">{req.release_reference_number || "—"}</div>
        </div>
        {req.release_method && (
          <div><span className="text-slate-500">Method: </span><span className="font-medium text-slate-800">{req.release_method}</span></div>
        )}
        {proofUrl && (
          <a href={proofUrl} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium" data-testid={`released-proof-${req.id}`}>
            <FileText size={12} /> View screenshot
          </a>
        )}
      </div>
    </div>
  );
}

function RejectedPopover({ req, onClose }) {
  const ref = useRef(null);
  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="absolute left-0 top-full mt-1 z-50 w-[260px] bg-white border border-rose-200 rounded-md shadow-xl p-3 text-xs"
      data-testid={`rejected-popover-${req.id}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="font-semibold text-rose-700">{req.payment_type} — rejected</div>
        <button onClick={onClose} className="p-0.5 hover:bg-slate-100 rounded"><X size={12} /></button>
      </div>
      <div className="space-y-1.5">
        <div className="text-slate-500 uppercase tracking-wider text-[10px]">Reason</div>
        <div className="text-slate-800 italic break-words whitespace-pre-wrap">
          {req.rejection_note ? `“${req.rejection_note}”` : "No reason provided."}
        </div>
        {req.decided_by_name && (
          <div className="pt-1 border-t border-rose-100 text-slate-500">
            Rejected by <span className="font-medium text-slate-700">{req.decided_by_name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

function RequestRow({ req }) {
  const [open, setOpen] = useState(false);
  const style = STATUS_PILL[req.status] || STATUS_PILL.pending;
  const { Icon } = style;
  const isReleased = req.status === "approved";
  const isRejected = req.status === "rejected";
  const clickable = isReleased || isRejected;
  const title = isReleased ? "Click to view UTR / screenshot" : isRejected ? "Click to view rejection note" : "";
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => clickable && setOpen((v) => !v)}
        disabled={!clickable}
        className={`w-full flex items-center justify-between gap-1.5 text-[10px] rounded border px-1.5 py-0.5 ${style.cls} ${clickable ? "cursor-pointer hover:brightness-95" : "cursor-default"}`}
        data-testid={`req-row-${req.id}`}
        title={title}
      >
        <span className="truncate font-medium text-left">{req.payment_type}</span>
        <span className="inline-flex items-center gap-0.5 shrink-0">
          <Icon size={10} weight="fill" /> {style.label}
        </span>
      </button>
      {open && isReleased && <ReleasedPopover req={req} onClose={() => setOpen(false)} />}
      {open && isRejected && <RejectedPopover req={req} onClose={() => setOpen(false)} />}
    </div>
  );
}

export function PaymentRequestsCell({ creator, requests }) {
  const list = (requests || []).slice().sort((a, b) => (b.requested_at || "").localeCompare(a.requested_at || ""));
  return (
    <div className="space-y-1 min-w-[150px]" data-testid={`payment-cell-${creator.id}`}>
      <PaymentStatusBadge value={creator.payment_status} />
      {list.length > 0 && (
        <div className="space-y-0.5 pt-0.5">
          {list.map((r) => <RequestRow key={r.id} req={r} />)}
        </div>
      )}
    </div>
  );
}
